/**
 * Gas Relayer Client for StealthOS
 *
 * Integrates with the existing gas-relayer service for anonymous EOA submission.
 * For local development, HMAC is disabled on the server sides.
 */

import {
  decryptPrivateRecord,
  encryptPrivateRecord,
  isEncryptedPrivateRecord,
} from "../api/private-data-storage";
import { createStoredProxyAuthHeaders } from "./proxy-auth";
import { withDeadline } from "./withDeadline";
import { EVM_CONFIG } from "../config";

// Engine proxy-aware URL for /api/v1/relay/* routes: the Base overrides for
// the Base deployment, the network's own engine proxy for a registry
// network (DogeOS Chikyu).
const RELAY_V1_BACKEND_URL = EVM_CONFIG.NETWORK?.registryKey
  ? EVM_CONFIG.PROVER_URL
  : process.env.REACT_APP_RELAY_V1_URL ||
    process.env.REACT_APP_BASE_ENGINE_PROXY_URL ||
    "/api/proxy/base-engine?path=";

const PERPS_VAULT_WITHDRAW_TIMEOUT_MS = 120_000;

function buildBackendUrl(base: string, endpoint: string): string {
  const normalizedBase = (base || "").replace(/\/+$/, "");
  if (normalizedBase.includes("?path=")) {
    return `${normalizedBase}${endpoint.replace(/^\//, "")}`;
  }
  return `${normalizedBase}${endpoint}`;
}

/**
 * Create simple headers (no HMAC needed for local dev)
 */
function createHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...createStoredProxyAuthHeaders(),
  };
}

type PrivateFeeRequestOptions = {
  signal?: AbortSignal;
  requestKey?: string;
  pool?: string;
  authHeaders?: Record<string, string>;
};

export async function requestPrivateFees<T>(
  action: string,
  body: unknown,
  options: PrivateFeeRequestOptions = {},
): Promise<T> {
  if (
    !/^(config|prepare|authorize|submit|recover|recover-head|history|deposit-prepare|deposit-authorize|deposit-merge-prepare|deposit-merge-backup|deposit-merge-authorize|confirm|swap-quote|swap-authorize|swap-records|swap-resume|bridge-routes|bridge-quote|bridge-authorize|bridge-records|bridge-resume|bridge-status)$/.test(
      action,
    )
  )
    throw new Error("Unsupported fee operation");
  if (/^deposit-merge-(prepare|backup|authorize)$/.test(action))
    return withDeadline(
      (signal) => privateFeeRequest<T>(action, body, { ...options, signal }),
      "Deposit merge preparation timed out. Check recovery before retrying.",
      options.signal,
      60_000,
    );
  // These routes prepare data; none broadcasts a transaction. Submission and
  // reconciliation keep their existing journal-based handling of uncertain outcomes.
  if (
    /^(config|prepare|authorize|deposit-prepare|deposit-authorize|swap-quote|swap-authorize|bridge-quote|bridge-authorize)$/.test(
      action,
    )
  )
    return withDeadline(
      (signal) => privateFeeRequest<T>(action, body, { ...options, signal }),
      "Transaction preparation timed out. No transaction was sent. Please try again.",
      options.signal,
      60_000,
    );
  if (
    /^(recover|recover-head|history|swap-records|bridge-records|bridge-status|bridge-routes)$/.test(
      action,
    )
  )
    return withDeadline(
      (signal) => privateFeeRequest<T>(action, body, { ...options, signal }),
      "Balance service is taking too long. Please try again.",
      options.signal,
      20_000,
    );
  return privateFeeRequest<T>(action, body, options);
}

async function privateFeeRequest<T>(
  action: string,
  body: unknown,
  options: PrivateFeeRequestOptions,
): Promise<T> {
  // Recovery callers already authenticated their exact wallet/privacy scope.
  // An explicitly missing token must never fall back to another saved account.
  if (
    options.authHeaders !== undefined &&
    !/^Bearer \S+$/.test(options.authHeaders.Authorization || "")
  )
    throw new Error(
      "Sign in to the current account before loading private activity or recovery records",
    );
  const response = await fetch(
    buildBackendUrl(
      RELAY_V1_BACKEND_URL,
      `/api/v1/relay/private-fees/${action}`,
    ),
    {
      method: "POST",
      headers: {
        ...(options.authHeaders === undefined
          ? createHeaders()
          : {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: options.authHeaders.Authorization,
            }),
        ...(options.requestKey
          ? { "Idempotency-Key": options.requestKey }
          : {}),
      },
      body: JSON.stringify(
        options.pool ? { ...(body as object), pool: options.pool } : body,
      ),
      signal: options.signal,
    },
  );
  const result = await response.json();
  if (!response.ok || result.success === false)
    throw Object.assign(
      new Error(
        typeof result.error === "string"
          ? result.error
          : "Transaction service is unavailable",
      ),
      result.code === "FEE_ESTIMATE_CHANGED" ? { code: result.code } : {},
    );
  return result;
}

export async function requestSupportedTokens(): Promise<{
  success: boolean;
  chainId: number;
  addresses: string[];
}> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/supported-tokens"),
    {
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: AbortSignal.timeout(15000),
    },
  );
  if (!response.ok) throw Error("Supported tokens could not be loaded.");
  return response.json();
}

function createRequestHeaders(
  extra?: Record<string, string>,
): Record<string, string> {
  return {
    ...createHeaders(),
    ...(extra || {}),
  };
}

function createExplicitAuthHeaders(
  authHeaders: Record<string, string>,
): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...authHeaders,
  };
}

function createIdempotencyKey(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function canonicalIdempotencyPart(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return `[${value.map(canonicalIdempotencyPart).join(",")}]`;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${canonicalIdempotencyPart(record[key])}`,
      )
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

async function sha256Hex(input: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const data = new TextEncoder().encode(input);
    const digest = await subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return hash.toString(16).padStart(16, "0");
}

async function stableIdempotencyKey(
  prefix: string,
  parts: unknown[],
): Promise<string> {
  const input = parts.map(canonicalIdempotencyPart).join("|");
  const hash = await sha256Hex(`${prefix}:${input}`);
  return `${prefix}-${hash.slice(0, 32)}`;
}

async function parseJsonOrText(response: Response): Promise<any> {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

/**
 * Check relayer service health
 */
export async function checkRelayerHealth(): Promise<boolean> {
  try {
    const healthUrl = buildBackendUrl(
      RELAY_V1_BACKEND_URL,
      "/api/v1/relayer/health",
    );
    console.log(`🏥 [RELAYER] Checking health at: ${healthUrl}`);

    const response = await fetch(healthUrl, {
      headers: {
        Accept: "application/json",
        ...createStoredProxyAuthHeaders(),
      },
    });

    if (!response.ok) {
      console.error(`❌ [RELAYER] Health check failed: ${response.status}`);
      return false;
    }

    const result = await response.json();
    const isHealthy = result.status === "healthy" || result.healthy === true;

    if (isHealthy) {
      console.log("✅ [RELAYER] Gas relayer is healthy");
    } else {
      console.warn(
        "⚠️ [RELAYER] Gas relayer responded but not healthy:",
        result.status ?? result.healthy,
      );
    }

    return isHealthy;
  } catch (error: any) {
    console.error("❌ [RELAYER] Health check error:", error.message);
    return false;
  }
}

export interface BaseRelayerHealthResponse {
  healthy?: boolean;
  status?: string;
  chain_id?: number;
  chainId?: number;
  perps_execution_fee_wei?: string | number;
  perpsExecutionFeeWei?: string | number;
  perps_target_uses_synthetix_oracle?: boolean;
  perpsTargetUsesSynthetixOracle?: boolean;
}

export async function fetchBaseRelayerHealth(): Promise<BaseRelayerHealthResponse | null> {
  try {
    const response = await fetch(
      buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relayer/health"),
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...createStoredProxyAuthHeaders(),
        },
      },
    );
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

export async function getBasePerpsRelayConfig(): Promise<{
  executionFeeWei: bigint;
  usesSynthetixOracle: boolean;
}> {
  const health = await fetchBaseRelayerHealth();
  if (!health) {
    throw new Error(
      "Base relayer health unavailable; cannot bind perps execution fee",
    );
  }
  const feeRaw =
    health?.perps_execution_fee_wei ?? health?.perpsExecutionFeeWei ?? 0;
  return {
    executionFeeWei: BigInt(String(feeRaw || 0)),
    usesSynthetixOracle:
      (health?.perps_target_uses_synthetix_oracle ??
        health?.perpsTargetUsesSynthetixOracle ??
        false) === true,
  };
}

/**
 * Get relayer's EOA address (for gas payments)
 */
export async function getRelayerAddress(): Promise<string | null> {
  try {
    const response = await fetch(
      buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relayer/address"),
      {
        method: "GET",
        headers: createHeaders(),
      },
    );

    if (!response.ok) {
      console.error(`❌ [RELAYER] Failed to get address: ${response.status}`);
      return null;
    }

    const data = await response.json();
    return data.eoaAddress || data.eoa_address || null;
  } catch (error: any) {
    console.error("❌ [RELAYER] Get address error:", error.message);
    return null;
  }
}

/**
 * Get relayer's stealth address (0zk... for shielded fee payments)
 */
export async function getRelayerStealthAddress(): Promise<string | null> {
  try {
    // First check env var
    const envAddress = process.env.REACT_APP_RELAYER_STEALTH_ADDRESS;
    if (envAddress) {
      return envAddress;
    }

    // Fallback to API call
    const response = await fetch(
      buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relayer/address"),
      {
        method: "GET",
        headers: createHeaders(),
      },
    );

    if (!response.ok) {
      console.error(
        `❌ [RELAYER] Failed to get stealth address: ${response.status}`,
      );
      return null;
    }

    const data = await response.json();
    return (
      data.stealthAddress || data.stealth_address || data.railgunAddress || null
    );
  } catch (error: any) {
    console.error("❌ [RELAYER] Get stealth address error:", error.message);
    return null;
  }
}

/**
 * Estimate relayer fees for a transaction
 */
export async function estimateRelayerFee(params: {
  chainId: number;
  tokenAddress: string;
  amount: string;
  gasEstimate?: string;
}): Promise<any> {
  try {
    console.log("💰 [RELAYER] Estimating fees:", params);

    const response = await fetch(
      buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/estimate-fee"),
      {
        method: "POST",
        headers: createHeaders(),
        body: JSON.stringify({
          chain_id: params.chainId,
          token_address: params.tokenAddress,
          amount: params.amount,
          gas_estimate: params.gasEstimate,
        }),
      },
    );

    if (!response.ok) {
      const error = await parseJsonOrText(response);
      console.error("❌ [RELAYER] Fee estimation failed:", error);
      return null;
    }

    const result = await response.json();
    console.log("✅ [RELAYER] Fee estimated:", result.feeEstimate || result);
    return result.feeEstimate || result;
  } catch (error: any) {
    console.error("❌ [RELAYER] Fee estimation error:", error.message);
    return null;
  }
}

/**
 * Submit transaction through gas relayer
 */
export async function submitRelayedTransaction(params: {
  chainId: number;
  serializedTransaction: string;
  tokenAddress?: string;
  amount?: string;
  userAddress?: string;
  feeDetails?: {
    relayerFee?: string;
    gasFee?: string;
    totalFee?: string;
    relayerAddress?: string;
    chainId?: number;
    tokenAddress?: string;
    amount?: string;
    userAddress?: string;
  };
  gasEstimate?: string;
}): Promise<{ transactionHash: string; gasUsed?: string; success: boolean }> {
  console.log("🚀 [RELAYER] Submitting transaction through gas relayer:", {
    chainId: params.chainId,
    serializedTxLength: params.serializedTransaction?.length,
    hasTokenAddress: Boolean(params.tokenAddress),
    hasAmount: Boolean(params.amount),
    hasUserAddress: Boolean(params.userAddress),
    feeDetailsPresent: Boolean(params.feeDetails),
    gasEstimate: params.gasEstimate,
  });

  let boundFeeDetails: Record<string, unknown> | undefined;
  if (params.feeDetails) {
    const relayerAddress =
      params.feeDetails.relayerAddress || (await getRelayerAddress());
    if (!relayerAddress) {
      throw new Error(
        "Relayer address unavailable; refusing unbound relay submission",
      );
    }
    boundFeeDetails = {
      relayer_fee: params.feeDetails.relayerFee,
      gas_fee: params.feeDetails.gasFee,
      total_fee: params.feeDetails.totalFee,
      relayer_address: relayerAddress,
      chain_id: params.feeDetails.chainId ?? params.chainId,
      token_address: params.feeDetails.tokenAddress ?? params.tokenAddress,
      amount: params.feeDetails.amount ?? params.amount,
      user_address: params.feeDetails.userAddress ?? params.userAddress,
    };
  }

  const idempotencyKey = await stableIdempotencyKey("base-relay-submit", [
    params.chainId,
    params.serializedTransaction,
    params.tokenAddress,
    params.amount,
    params.userAddress,
    boundFeeDetails,
    params.gasEstimate,
  ]);

  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/submit"),
    {
      method: "POST",
      headers: {
        ...createHeaders(),
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        chain_id: params.chainId,
        serialized_transaction: params.serializedTransaction,
        token_address: params.tokenAddress,
        amount: params.amount,
        user_address: params.userAddress,
        fee_details: boundFeeDetails,
        gas_estimate: params.gasEstimate,
      }),
    },
  );

  if (!response.ok) {
    const error = await parseJsonOrText(response);
    console.error("❌ [RELAYER] Submit failed with status:", response.status);
    console.error(
      "❌ [RELAYER] Error response:",
      JSON.stringify(error, null, 2),
    );
    console.error("❌ [RELAYER] Request params were:", {
      chainId: params.chainId,
      serializedTxLength: params.serializedTransaction?.length,
      hasTokenAddress: Boolean(params.tokenAddress),
      hasAmount: Boolean(params.amount),
      hasUserAddress: Boolean(params.userAddress),
      feeDetailsPresent: Boolean(params.feeDetails),
      gasEstimate: params.gasEstimate,
    });
    throw new Error(
      `Relay submission failed: ${error.error || error.message || JSON.stringify(error)}`,
    );
  }

  const result = await response.json();
  const transactionHash = result.transactionHash || result.transaction_hash;
  const gasUsed = result.gasUsed || result.gas_used;
  console.log("✅ [RELAYER] Transaction relayed:", transactionHash);

  return {
    transactionHash,
    gasUsed,
    success: result.success !== false,
  };
}

/** Public dimensions of a relayed withdrawal the relayer prices before the
 * proof binds the fee (DogeOS Chikyu; docs/EVM_POOL_RELAY.md). */
export interface PoolRelayQuoteBody {
  chainId: number;
  pool: string;
  kind: "withdraw" | "withdraw_batch";
  token: string;
  inputCount: number;
  outputCount: 0 | 1;
  recipients: string[];
}

export interface PoolConsolidationQuoteBody {
  chainId: number;
  pool: string;
  kind: "joinsplit";
  token: string;
  inputCount: 2;
  outputCount: 1;
  treeIndex: number;
}

export interface PoolSwapQuoteBody {
  chainId: number;
  pool: string;
  kind: "swap";
  token: string;
  outputToken: string;
  inputCount: number;
  outputCount: 0 | 1;
}

/** Read-only pricing: no note, proof, amount or balance leaves the browser,
 * and nothing is signed or submitted. */
export async function requestPoolRelayQuote(
  body: PoolRelayQuoteBody | PoolConsolidationQuoteBody | PoolSwapQuoteBody,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  return withDeadline(
    async (signal) => {
      const response = await fetch(
        buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/pool/quote"),
        {
          method: "POST",
          headers: createHeaders(),
          body: JSON.stringify(body),
          signal,
        },
      );
      const result = await parseJsonOrText(response);
      if (!response.ok || result?.success !== true)
        throw buildRelayerRequestError(
          response,
          result,
          `Network fee quote failed (${response.status})`,
        );
      return result.quote;
    },
    "The network fee quote is taking too long. Please try again.",
    options.signal,
    30_000,
  );
}

export async function submitBasePoolRelay(params: {
  chainId: number;
  serializedTransaction: string;
  tokenAddress?: string;
  amount?: string;
  userAddress?: string;
  feeDetails?: {
    relayerFee?: string;
    gasFee?: string;
    totalFee?: string;
    relayerAddress?: string;
    chainId?: number;
    tokenAddress?: string;
    amount?: string;
    userAddress?: string;
  };
  gasEstimate?: string;
  signal?: AbortSignal;
}): Promise<{ transactionHash: string; gasUsed?: string; success: boolean }> {
  let boundFeeDetails: Record<string, unknown> | undefined;
  if (params.feeDetails) {
    const relayerAddress =
      params.feeDetails.relayerAddress || (await getRelayerAddress());
    if (!relayerAddress) {
      throw new Error(
        "Relayer address unavailable; refusing unbound Base pool relay submission",
      );
    }
    boundFeeDetails = {
      relayer_fee: params.feeDetails.relayerFee,
      gas_fee: params.feeDetails.gasFee,
      total_fee: params.feeDetails.totalFee,
      relayer_address: relayerAddress,
      chain_id: params.feeDetails.chainId ?? params.chainId,
      token_address: params.feeDetails.tokenAddress ?? params.tokenAddress,
      amount: params.feeDetails.amount ?? params.amount,
      user_address: params.feeDetails.userAddress ?? params.userAddress,
    };
  }

  const idempotencyKey = await stableIdempotencyKey("base-pool-relay", [
    params.chainId,
    params.serializedTransaction,
    params.tokenAddress,
    params.amount,
    params.userAddress,
    boundFeeDetails,
  ]);

  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/pool"),
    {
      method: "POST",
      headers: {
        ...createHeaders(),
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        chain_id: params.chainId,
        serialized_transaction: params.serializedTransaction,
        token_address: params.tokenAddress,
        amount: params.amount,
        user_address: params.userAddress,
        fee_details: boundFeeDetails,
        gas_estimate: params.gasEstimate,
      }),
      signal: params.signal,
    },
  );

  if (!response.ok) {
    const error = await parseJsonOrText(response);
    throw buildRelayerRequestError(
      response,
      error,
      `Base pool relay failed (${response.status})`,
    );
  }

  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    gasUsed: result.gas_used || result.gasUsed,
    success: result.success !== false,
  };
}

/**
 * Convert string to hex (browser-compatible, no Buffer)
 */
function stringToHex(str: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Serialize a transaction for relay submission
 * Converts ethers transaction to hex-encoded JSON (same format as Lexie)
 */
export function serializeTransaction(tx: {
  to: string;
  data: string;
  value?: string;
  gasLimit?: string;
  gasPrice?: string;
  maxFeePerGas?: string;
  maxPriorityFeePerGas?: string;
  type?: number;
}): string {
  const txObj: Record<string, any> = {
    to: tx.to,
    data: tx.data,
    value: tx.value || "0x0",
    gasLimit: tx.gasLimit,
    gasPrice: tx.gasPrice,
    maxFeePerGas: tx.maxFeePerGas,
    maxPriorityFeePerGas: tx.maxPriorityFeePerGas,
    type: tx.type,
  };

  // Remove undefined values
  Object.keys(txObj).forEach((key) => {
    if (txObj[key] === undefined) {
      delete txObj[key];
    }
  });

  // Convert to hex-encoded JSON (browser-compatible)
  const jsonString = JSON.stringify(txObj);
  const hexString = stringToHex(jsonString);
  return "0x" + hexString;
}

/**
 * Check if relayer should be used (always try for privacy)
 */
export function shouldUseRelayer(_chainId: number, _amount: string): boolean {
  console.log("🚀 [RELAYER] Will attempt gas relayer for complete privacy");
  return true;
}

/**
 * Calculate relayer fee (0.5% of amount)
 */
export function calculateRelayerFee(amount: bigint, basisPoints = 50n): bigint {
  return (amount * basisPoints) / 10000n;
}

export interface BaseSwapPlanParams {
  chainId: number;
  userAddress: string;
  inputToken: string;
  outputToken: string;
  inputAmount: string;
  minOutputAmount: string;
  odosPathId: string;
  authHeaders?: Record<string, string>;
}

export interface BaseSwapPlanResponse {
  success: boolean;
  provider: string;
  swapRouter: string;
  swapCallData: string;
  swapCallValue: string;
  protocolSwapFeeBps: number;
  partnerSwapFeeBps: number;
  protocolFeeRecipient: string;
  partnerFeeRecipient: string;
  requiredGrossOutput: string;
  surplusRecipient: string;
}

export async function requestBaseSwapPlan(
  params: BaseSwapPlanParams,
): Promise<BaseSwapPlanResponse> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/swap/plan"),
    {
      method: "POST",
      headers:
        params.authHeaders === undefined
          ? createHeaders()
          : createExplicitAuthHeaders(params.authHeaders),
      body: JSON.stringify({
        chain_id: params.chainId,
        user_address: params.userAddress,
        input_token: params.inputToken,
        output_token: params.outputToken,
        input_amount: params.inputAmount,
        min_output_amount: params.minOutputAmount,
        odos_path_id: params.odosPathId,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw buildRelayerRequestError(
      response,
      error,
      `Swap plan failed (${response.status})`,
    );
  }

  const result = await response.json();
  return {
    success: result.success !== false,
    provider: result.provider,
    swapRouter: result.swap_router || result.swapRouter,
    swapCallData: result.swap_call_data || result.swapCallData,
    swapCallValue: result.swap_call_value || result.swapCallValue,
    protocolSwapFeeBps: Number(
      result.protocol_swap_fee_bps ?? result.protocolSwapFeeBps,
    ),
    partnerSwapFeeBps: Number(
      result.partner_swap_fee_bps ?? result.partnerSwapFeeBps,
    ),
    protocolFeeRecipient:
      result.protocol_fee_recipient || result.protocolFeeRecipient,
    partnerFeeRecipient:
      result.partner_fee_recipient || result.partnerFeeRecipient,
    requiredGrossOutput:
      result.required_gross_output || result.requiredGrossOutput,
    surplusRecipient: result.surplus_recipient || result.surplusRecipient,
  };
}

export interface BridgeQuoteParams {
  sourceChainId: number;
  sourceToken: string;
  sourceAmount: string;
  sourceAmountIsNet?: boolean;
  destinationChainId: number;
  destinationToken: string;
  destinationRecipient: string;
  sourceAuthority: string;
  destinationAuthority: string;
  minDestinationAmount?: string;
  referralCode?: string;
  affiliateFeePercent?: string;
  prependOperatingExpenses?: boolean;
  allowPartialFill?: boolean;
  destinationPrivateRecipient?: {
    recipientKey: string;
    recipientMpk: string;
    destinationInboxPublicKey: string;
    recipientHandle?: string;
    handleVersion?: number;
  };
  idempotencyKey?: string;
  signal?: AbortSignal;
}

function buildRelayerRequestError(
  response: Response,
  error: any,
  fallbackMessage: string,
): Error & {
  status?: number;
  details?: any;
} {
  const message = error?.error || error?.message || fallbackMessage;
  const enriched = new Error(message) as Error & {
    status?: number;
    details?: any;
  };
  enriched.status = response.status;
  enriched.details = error;
  return enriched;
}

export async function submitBridgeQuote(
  params: BridgeQuoteParams,
): Promise<any> {
  const idempotencyKey =
    params.idempotencyKey ||
    (await stableIdempotencyKey("bridge-quote", [
      params.sourceChainId,
      params.sourceToken,
      params.sourceAmount,
      params.sourceAmountIsNet,
      params.destinationChainId,
      params.destinationToken,
      params.destinationRecipient,
      params.sourceAuthority,
      params.destinationAuthority,
      params.minDestinationAmount,
      params.referralCode,
      params.affiliateFeePercent,
      params.prependOperatingExpenses,
      params.allowPartialFill,
      params.destinationPrivateRecipient,
    ]));
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/bridge/quote"),
    {
      method: "POST",
      headers: {
        ...createHeaders(),
        "Idempotency-Key": idempotencyKey,
      },
      signal: params.signal,
      body: JSON.stringify({
        source_chain_id: params.sourceChainId,
        source_token: params.sourceToken,
        source_amount: params.sourceAmount,
        source_amount_is_net: params.sourceAmountIsNet,
        destination_chain_id: params.destinationChainId,
        destination_token: params.destinationToken,
        destination_recipient: params.destinationRecipient,
        source_authority: params.sourceAuthority,
        destination_authority: params.destinationAuthority,
        min_destination_amount: params.minDestinationAmount,
        referral_code: params.referralCode,
        affiliate_fee_percent: params.affiliateFeePercent,
        prepend_operating_expenses: params.prependOperatingExpenses,
        allow_partial_fill: params.allowPartialFill,
        destination_private_recipient: params.destinationPrivateRecipient,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.error ||
        error.message ||
        `Bridge quote failed (${response.status})`,
    );
  }
  return response.json();
}

export interface BridgeExecuteParams {
  chainId: number;
  dlnOrderId?: string;
  sourceWithdrawSerializedTx?: string;
  sourceAuthority?: string;
  idempotencyKey?: string;
}

export interface BridgeExecuteResponse {
  success: boolean;
  transactionHash?: string;
  sourceWithdrawTxHash?: string;
  orderId?: string;
  bridgedAmount?: string;
  changeLeafIndex?: number | null;
  status?: string;
  mappedState?: string;
  [key: string]: any;
}

export async function submitBridgeExecute(
  params: BridgeExecuteParams,
): Promise<BridgeExecuteResponse> {
  const idempotencyKey =
    params.idempotencyKey ||
    (await stableIdempotencyKey("bridge-execute", [
      params.chainId,
      params.dlnOrderId,
      params.sourceAuthority,
      params.sourceWithdrawSerializedTx,
    ]));
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/bridge/execute"),
    {
      method: "POST",
      headers: {
        ...createHeaders(),
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        chain_id: params.chainId,
        dln_order_id: params.dlnOrderId,
        source_authority: params.sourceAuthority,
        source_withdraw_serialized_tx: params.sourceWithdrawSerializedTx,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw buildRelayerRequestError(
      response,
      error,
      `Bridge execute failed (${response.status})`,
    );
  }
  const result = await response.json();
  return {
    ...result,
    success: result.success !== false,
    transactionHash:
      result.transaction_hash ||
      result.transactionHash ||
      result.source_withdraw_tx_hash ||
      result.sourceWithdrawTxHash,
    sourceWithdrawTxHash:
      result.source_withdraw_tx_hash ||
      result.sourceWithdrawTxHash ||
      result.transaction_hash ||
      result.transactionHash,
    orderId: result.order_id || result.orderId,
    bridgedAmount: result.bridged_amount || result.bridgedAmount,
    changeLeafIndex: result.change_leaf_index ?? result.changeLeafIndex,
    mappedState: result.mapped_state || result.mappedState,
  };
}

export async function getBridgeStatus(
  orderId: string,
  sourceAuthority?: string,
): Promise<any> {
  const endpoint = sourceAuthority
    ? `/api/v1/relay/bridge/status/${orderId}?source_authority=${encodeURIComponent(sourceAuthority)}`
    : `/api/v1/relay/bridge/status/${orderId}`;
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, endpoint),
    {
      method: "GET",
      headers: createHeaders(),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.error ||
        error.message ||
        `Bridge status failed (${response.status})`,
    );
  }
  return response.json();
}

export async function submitBridgeCancel(
  orderId: string,
  sourceAuthority?: string,
): Promise<any> {
  const idempotencyKey = await stableIdempotencyKey("bridge-cancel", [
    orderId,
    sourceAuthority,
  ]);
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/bridge/cancel"),
    {
      method: "POST",
      headers: {
        ...createHeaders(),
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        order_id: orderId,
        source_authority: sourceAuthority,
      }),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.error ||
        error.message ||
        `Bridge cancel failed (${response.status})`,
    );
  }
  return response.json();
}

export async function submitBridgeSettle(
  orderId: string,
  destinationPrivateRecipient?: {
    recipientKey: string;
    recipientMpk: string;
    destinationInboxPublicKey: string;
    recipientHandle?: string;
    handleVersion?: number;
  },
  privateRecipientIntentId?: string,
): Promise<any> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/bridge/settle"),
    {
      method: "POST",
      headers: {
        ...createHeaders(),
        "Idempotency-Key": `bridge-settle-${orderId}`,
      },
      body: JSON.stringify({
        order_id: orderId,
        destination_private_recipient: destinationPrivateRecipient,
        private_recipient_intent_id: privateRecipientIntentId,
      }),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw buildRelayerRequestError(
      response,
      error,
      `Bridge settle failed (${response.status})`,
    );
  }
  return response.json();
}

export async function submitPerpsDelegate(params: {
  chainId: number;
  vaultAddress: string;
  userAddress?: string;
  executor: string;
  permission: string; // bytes32 hex
  expiry: number; // unix seconds
  ownershipProof: string; // hex bytes
  idempotencyKey?: string;
}): Promise<{ transactionHash: string; success: boolean }> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/perps/delegate"),
    {
      method: "POST",
      headers: createRequestHeaders({
        "Idempotency-Key":
          params.idempotencyKey ||
          (await stableIdempotencyKey("perps-delegate", [
            params.chainId,
            params.vaultAddress,
            params.userAddress,
            params.executor,
            params.permission,
            params.expiry,
            params.ownershipProof,
          ])),
      }),
      body: JSON.stringify({
        chain_id: params.chainId,
        vault_address: params.vaultAddress,
        user_address: params.userAddress,
        executor: params.executor,
        permission: params.permission,
        expiry: params.expiry,
        ownership_proof: params.ownershipProof,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Perps delegate failed: ${error.error || response.status}`);
  }
  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    success: result.success !== false,
  };
}

export type PerpsAccountProfile = {
  protocol?: string;
  vaultAddress?: string;
  accountNft?: string;
  accountTokenId?: string;
  accountId?: string;
  permissionManager?: string;
  ownershipVerifier?: string;
  ownershipCommitment?: string;
  ownershipSalt?: string;
  marketProxy?: string;
  marketId?: string;
  collateralAddress?: string;
  profiles?: Record<string, unknown>;
  chainScope?: string;
  provider?: string;
  authorityPublicKey?: string;
  authoritySecretKeyBase58?: string;
  userAccount?: string;
  userStats?: string;
  collateralMint?: string;
  lastSelectedMarket?: string;
};

type PrivateRecordDecryptContext = {
  // Every key the account has been addressed under, newest first: see
  // getInboxViewingKeyCandidates in utils/inbox-crypto.
  viewingKeyHex: string | string[];
  legacyMpkHex?: string;
  inboxSecretKey?: Uint8Array | Uint8Array[];
};

type PrivateRecordEncryptTarget = {
  inboxPublicKeyHex?: string | null;
  viewingKeyHex?: string | null;
};

export async function loadPerpsAccountProfile(
  address: string,
  authHeaders: Record<string, string>,
  decryptContext: PrivateRecordDecryptContext,
): Promise<{
  success: boolean;
  found: boolean;
  profile?: PerpsAccountProfile;
  updatedAtUnix?: number;
}> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, `/api/v1/perps/account/${address}`),
    {
      method: "GET",
      headers: createExplicitAuthHeaders(authHeaders),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Load perps account profile failed: ${error.error || response.status}`,
    );
  }
  const result = await response.json();
  let profile = result.profile;
  if (profile && isEncryptedPrivateRecord(profile)) {
    profile = await decryptPrivateRecord<PerpsAccountProfile>(
      profile,
      decryptContext,
    );
  }
  return {
    success: result.success !== false,
    found: result.found === true,
    profile,
    updatedAtUnix: result.updated_at_unix ?? result.updatedAtUnix,
  };
}

export async function savePerpsAccountProfile(
  address: string,
  profile: PerpsAccountProfile,
  authHeaders: Record<string, string>,
  encryptTarget: PrivateRecordEncryptTarget,
): Promise<{ success: boolean; updatedAtUnix?: number }> {
  const encryptedProfile = await encryptPrivateRecord(profile, encryptTarget);
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, `/api/v1/perps/account/${address}`),
    {
      method: "PUT",
      headers: createExplicitAuthHeaders(authHeaders),
      body: JSON.stringify({ profile: encryptedProfile }),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Save perps account profile failed: ${error.error || response.status}`,
    );
  }
  const result = await response.json();
  return {
    success: result.success !== false,
    updatedAtUnix: result.updated_at_unix ?? result.updatedAtUnix,
  };
}

export async function submitPerpsDeploy(params: {
  chainId: number;
  vaultOwner: string;
  accountNft?: string;
  accountTokenId?: number;
  accountId?: number;
  permissionManager?: string;
  ownershipVerifier: string;
  ownershipCommitment: string; // bytes32 hex
  perpsMarketProxy?: string;
  deploySalt?: string;
  idempotencyKey?: string;
}): Promise<{ transactionHash: string; success: boolean }> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/perps/deploy"),
    {
      method: "POST",
      headers: createRequestHeaders({
        "Idempotency-Key":
          params.idempotencyKey ||
          (await stableIdempotencyKey("perps-deploy", [
            params.chainId,
            params.vaultOwner,
            params.accountNft,
            params.accountTokenId,
            params.accountId,
            params.permissionManager,
            params.ownershipVerifier,
            params.ownershipCommitment,
            params.perpsMarketProxy,
            params.deploySalt,
          ])),
      }),
      body: JSON.stringify({
        chain_id: params.chainId,
        vault_owner: params.vaultOwner,
        account_nft: params.accountNft,
        account_token_id: params.accountTokenId,
        account_id: params.accountId,
        permission_manager: params.permissionManager,
        ownership_verifier: params.ownershipVerifier,
        ownership_commitment: params.ownershipCommitment,
        perps_market_proxy: params.perpsMarketProxy,
        deploy_salt: params.deploySalt,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Perps deploy failed: ${error.error || response.status}`);
  }
  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    success: result.success !== false,
  };
}

export async function submitPerpsCreateAccount(params: {
  chainId: number;
  vaultOwner: string;
  accountNft?: string;
  accountTokenId?: number;
  accountId?: number;
  permissionManager?: string;
  ownershipVerifier: string;
  ownershipCommitment: string;
  perpsMarketProxy?: string;
  deploySalt?: string;
  autoCreateAccount?: boolean;
  requestedAccountId?: number;
  idempotencyKey?: string;
}): Promise<{
  transactionHash: string;
  success: boolean;
  vaultAddress?: string;
  accountNft?: string;
  accountTokenId?: string;
  accountId?: string;
  accountCreateTxHash?: string;
}> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/perps/create-account"),
    {
      method: "POST",
      headers: createRequestHeaders({
        "Idempotency-Key":
          params.idempotencyKey ||
          (await stableIdempotencyKey("perps-create-account", [
            params.chainId,
            params.vaultOwner,
            params.accountNft,
            params.accountTokenId,
            params.accountId,
            params.permissionManager,
            params.ownershipVerifier,
            params.ownershipCommitment,
            params.perpsMarketProxy,
            params.deploySalt,
            params.autoCreateAccount,
            params.requestedAccountId,
          ])),
      }),
      body: JSON.stringify({
        chain_id: params.chainId,
        vault_owner: params.vaultOwner,
        account_nft: params.accountNft,
        account_token_id: params.accountTokenId,
        account_id: params.accountId,
        permission_manager: params.permissionManager,
        ownership_verifier: params.ownershipVerifier,
        ownership_commitment: params.ownershipCommitment,
        auto_create_account: params.autoCreateAccount ?? true,
        requested_account_id: params.requestedAccountId,
        perps_market_proxy: params.perpsMarketProxy,
        deploy_salt: params.deploySalt,
      }),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Perps create-account failed: ${error.error || response.status}`,
    );
  }
  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    success: result.success !== false,
    vaultAddress: result.vault_address || result.vaultAddress,
    accountNft: result.account_nft || result.accountNft,
    accountTokenId:
      (result.account_token_id ?? result.accountTokenId)?.toString?.() ??
      undefined,
    accountId:
      (result.account_id ?? result.accountId)?.toString?.() ?? undefined,
    accountCreateTxHash:
      result.account_create_tx_hash || result.accountCreateTxHash,
  };
}

export async function submitPerpsTrade(params: {
  chainId: number;
  vaultAddress: string;
  userAddress?: string;
  action: "commit" | "cancel";
  expiry: number;
  actionCallData: string;
  ownershipProof: string;
  idempotencyKey?: string;
}): Promise<{ transactionHash: string; success: boolean }> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/perps/trade"),
    {
      method: "POST",
      headers: createRequestHeaders({
        "Idempotency-Key":
          params.idempotencyKey ||
          (await stableIdempotencyKey("perps-trade", [
            params.chainId,
            params.vaultAddress,
            params.userAddress,
            params.action,
            params.expiry,
            params.actionCallData,
            params.ownershipProof,
          ])),
      }),
      body: JSON.stringify({
        chain_id: params.chainId,
        vault_address: params.vaultAddress,
        user_address: params.userAddress,
        action: params.action,
        expiry: params.expiry,
        action_call_data: params.actionCallData,
        ownership_proof: params.ownershipProof,
      }),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Perps trade failed: ${error.error || response.status}`);
  }
  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    success: result.success !== false,
  };
}

export async function submitPerpsMargin(params: {
  chainId: number;
  vaultAddress: string;
  userAddress?: string;
  direction: "deposit" | "withdraw";
  expiry: number;
  modifyCollateralCallData: string;
  feeCollateralToken?: string;
  withdrawRecipient?: string;
  spotSynthMarketId?: number;
  privateReshield?: boolean;
  outputNpk?: string;
  outputTokenHash?: string;
  outputCommitment?: string;
  outputRecoveryKey?: string;
  outputEncryptedNote?: string;
  expectedOutputAmount?: string;
  reshieldOwnershipProof?: string;
  ownershipProof: string;
  idempotencyKey?: string;
}): Promise<{
  transactionHash: string;
  success: boolean;
  status?: string;
  code?: string;
  recoveryAction?: string;
  recoveryRoute?: string;
  vaultAddress?: string;
  assetToken?: string;
  expectedOutputAmount?: string;
  actualOutputAmount?: string;
  withdrawTxHash?: string;
  depositTxHash?: string;
  outputAmount?: string;
  outputCommitment?: string;
  outputTokenHash?: string;
  leafIndex?: number;
  reshielded?: boolean;
  error?: string;
}> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/perps/margin"),
    {
      method: "POST",
      headers: createRequestHeaders({
        "Idempotency-Key":
          params.idempotencyKey ||
          (await stableIdempotencyKey("perps-margin", [
            params.chainId,
            params.vaultAddress,
            params.userAddress,
            params.direction,
            params.expiry,
            params.modifyCollateralCallData,
            params.feeCollateralToken,
            params.withdrawRecipient,
            params.spotSynthMarketId,
            params.privateReshield,
            params.outputNpk,
            params.outputTokenHash,
            params.outputCommitment,
            params.outputRecoveryKey,
            params.outputEncryptedNote,
            params.expectedOutputAmount,
            params.reshieldOwnershipProof,
            params.ownershipProof,
          ])),
      }),
      body: JSON.stringify({
        chain_id: params.chainId,
        vault_address: params.vaultAddress,
        user_address: params.userAddress,
        direction: params.direction,
        expiry: params.expiry,
        modify_collateral_call_data: params.modifyCollateralCallData,
        fee_collateral_token: params.feeCollateralToken,
        withdraw_recipient: params.withdrawRecipient,
        spot_synth_market_id: params.spotSynthMarketId,
        private_reshield: params.privateReshield,
        output_npk: params.outputNpk,
        output_token_hash: params.outputTokenHash,
        output_commitment: params.outputCommitment,
        output_recovery_key: params.outputRecoveryKey,
        output_encrypted_note: params.outputEncryptedNote,
        expected_output_amount: params.expectedOutputAmount,
        reshield_ownership_proof: params.reshieldOwnershipProof,
        ownership_proof: params.ownershipProof,
      }),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Perps margin failed: ${error.error || response.status}`);
  }
  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    success: result.success !== false,
    status: result.status,
    code: result.code,
    recoveryAction: result.recovery_action || result.recoveryAction,
    recoveryRoute: result.recovery_route || result.recoveryRoute,
    vaultAddress: result.vault_address || result.vaultAddress,
    assetToken: result.asset_token || result.assetToken,
    expectedOutputAmount:
      result.expected_output_amount || result.expectedOutputAmount,
    actualOutputAmount:
      result.actual_output_amount || result.actualOutputAmount,
    withdrawTxHash: result.withdraw_tx_hash || result.withdrawTxHash,
    depositTxHash: result.deposit_tx_hash || result.depositTxHash,
    outputAmount: result.output_amount || result.outputAmount,
    outputCommitment: result.output_commitment || result.outputCommitment,
    outputTokenHash: result.output_token_hash || result.outputTokenHash,
    leafIndex:
      typeof result.leaf_index === "number"
        ? result.leaf_index
        : result.leafIndex,
    reshielded: result.reshielded === true,
    error: result.error,
  };
}

export async function submitPerpsVaultWithdraw(params: {
  chainId: number;
  vaultAddress: string;
  userAddress?: string;
  expiry: number;
  feeCollateralToken?: string;
  outputNpk?: string;
  outputTokenHash?: string;
  outputCommitment?: string;
  outputRecoveryKey?: string;
  outputEncryptedNote?: string;
  expectedOutputAmount?: string;
  ownershipProof: string;
  idempotencyKey?: string;
}): Promise<{
  transactionHash: string;
  success: boolean;
  withdrawTxHash?: string;
  depositTxHash?: string;
  outputAmount?: string;
  outputCommitment?: string;
  outputTokenHash?: string;
  leafIndex?: number;
  reshielded?: boolean;
}> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => {
    controller.abort();
  }, PERPS_VAULT_WITHDRAW_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(
      buildBackendUrl(
        RELAY_V1_BACKEND_URL,
        "/api/v1/relay/perps/vault-withdraw",
      ),
      {
        method: "POST",
        signal: controller.signal,
        headers: createRequestHeaders({
          "Idempotency-Key":
            params.idempotencyKey ||
            (await stableIdempotencyKey("perps-vault-withdraw", [
              params.chainId,
              params.vaultAddress,
              params.userAddress,
              params.expiry,
              params.feeCollateralToken,
              params.outputNpk,
              params.outputTokenHash,
              params.outputCommitment,
              params.outputRecoveryKey,
              params.outputEncryptedNote,
              params.expectedOutputAmount,
              params.ownershipProof,
            ])),
        }),
        body: JSON.stringify({
          chain_id: params.chainId,
          vault_address: params.vaultAddress,
          user_address: params.userAddress,
          expiry: params.expiry,
          fee_collateral_token: params.feeCollateralToken,
          output_npk: params.outputNpk,
          output_token_hash: params.outputTokenHash,
          output_commitment: params.outputCommitment,
          output_recovery_key: params.outputRecoveryKey,
          output_encrypted_note: params.outputEncryptedNote,
          expected_output_amount: params.expectedOutputAmount,
          ownership_proof: params.ownershipProof,
        }),
      },
    );
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(
        "Perps vault withdraw timed out before the relayer returned. Refresh vault balances before retrying.",
      );
    }
    throw err;
  } finally {
    globalThis.clearTimeout(timeout);
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Perps vault-withdraw failed: ${error.error || response.status}`,
    );
  }
  const result = await response.json();
  const transactionHash = result.transaction_hash || result.transactionHash;
  if (
    response.status === 202 ||
    result.code === "TX_SUBMISSION_UNCERTAIN" ||
    result.status === "submitted_uncertain"
  ) {
    throw new Error(
      `Perps vault withdraw was submitted but is not confirmed yet${
        transactionHash ? ` (${transactionHash})` : ""
      }. Private balance will update only after the deposit transaction confirms onchain. Refresh vault balances before retrying.`,
    );
  }
  if (result.success === false) {
    throw new Error(
      `Perps vault-withdraw failed: ${
        result.message || result.error || result.code || response.status
      }`,
    );
  }
  if (!transactionHash) {
    throw new Error("Perps vault-withdraw failed: missing transaction hash");
  }
  return {
    transactionHash,
    success: true,
    withdrawTxHash: result.withdraw_tx_hash || result.withdrawTxHash,
    depositTxHash: result.deposit_tx_hash || result.depositTxHash,
    outputAmount: result.output_amount || result.outputAmount,
    outputCommitment: result.output_commitment || result.outputCommitment,
    outputTokenHash: result.output_token_hash || result.outputTokenHash,
    leafIndex:
      typeof result.leaf_index === "number"
        ? result.leaf_index
        : result.leafIndex,
    reshielded: result.reshielded === true,
  };
}

export async function submitPerpsRevoke(params: {
  chainId: number;
  vaultAddress: string;
  userAddress?: string;
  executor: string;
  permission: string; // bytes32 hex
  expiry: number; // unix seconds
  ownershipProof: string; // hex bytes
  idempotencyKey?: string;
}): Promise<{ transactionHash: string; success: boolean }> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/perps/revoke"),
    {
      method: "POST",
      headers: createRequestHeaders({
        "Idempotency-Key":
          params.idempotencyKey ||
          (await stableIdempotencyKey("perps-revoke", [
            params.chainId,
            params.vaultAddress,
            params.userAddress,
            params.executor,
            params.permission,
            params.expiry,
            params.ownershipProof,
          ])),
      }),
      body: JSON.stringify({
        chain_id: params.chainId,
        vault_address: params.vaultAddress,
        user_address: params.userAddress,
        executor: params.executor,
        permission: params.permission,
        expiry: params.expiry,
        ownership_proof: params.ownershipProof,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Perps revoke failed: ${error.error || response.status}`);
  }
  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    success: result.success !== false,
  };
}

export async function submitPerpsEscape(params: {
  chainId: number;
  vaultAddress: string;
  recipient: string;
  expiry: number;
  ownershipProof: string;
  idempotencyKey?: string;
}): Promise<{ transactionHash: string; success: boolean }> {
  const response = await fetch(
    buildBackendUrl(RELAY_V1_BACKEND_URL, "/api/v1/relay/perps/escape"),
    {
      method: "POST",
      headers: createRequestHeaders({
        "Idempotency-Key":
          params.idempotencyKey ||
          (await stableIdempotencyKey("perps-escape", [
            params.chainId,
            params.vaultAddress,
            params.recipient,
            params.expiry,
            params.ownershipProof,
          ])),
      }),
      body: JSON.stringify({
        chain_id: params.chainId,
        vault_address: params.vaultAddress,
        recipient: params.recipient,
        expiry: params.expiry,
        ownership_proof: params.ownershipProof,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Perps escape failed: ${error.error || response.status}`);
  }
  const result = await response.json();
  return {
    transactionHash: result.transaction_hash || result.transactionHash,
    success: result.success !== false,
  };
}

export default {
  checkRelayerHealth,
  fetchBaseRelayerHealth,
  getBasePerpsRelayConfig,
  getRelayerAddress,
  estimateRelayerFee,
  submitRelayedTransaction,
  submitBasePoolRelay,
  serializeTransaction,
  shouldUseRelayer,
  calculateRelayerFee,
  submitBridgeQuote,
  submitBridgeExecute,
  getBridgeStatus,
  submitBridgeCancel,
  submitBridgeSettle,
  loadPerpsAccountProfile,
  savePerpsAccountProfile,
  submitPerpsDelegate,
  submitPerpsDeploy,
  submitPerpsCreateAccount,
  submitPerpsTrade,
  submitPerpsMargin,
  submitPerpsVaultWithdraw,
  submitPerpsRevoke,
  submitPerpsEscape,
};
